/* eslint-disable eqeqeq */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  ButtonGroup,
  Table,
  Card,
  Modal,
} from "react-bootstrap";
import {
  FiPlus,
  FiAlertCircle,
  FiInfo,
  FiDollarSign,
  FiEdit,
  FiDownload,
} from "react-icons/fi";
import ReactExport from "react-export-excel";
import CustomNavbar from "../../components/CustomNavbar";
import SidebarBoarding from "../../components/SidebarDashboard";
import BudgetModal from "../../components/capacitacionDesarrollo/BudgetModal";
import {
  getBudgets,
  getAnnualBudgets,
  clearAnnualBudgets,
  updateMonthlyBudgetExpenditure,
} from "../../redux/capacitacionDesarrollo/BudgetControl/actions";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelSheet;
const ExcelColumn = ReactExport.ExcelColumn;

const months = [
  { id: 1, name: "Enero" },
  { id: 2, name: "Febrero" },
  { id: 3, name: "Marzo" },
  { id: 4, name: "Abril" },
  { id: 5, name: "Mayo" },
  { id: 6, name: "Junio" },
  { id: 7, name: "Julio" },
  { id: 8, name: "Agosto" },
  { id: 9, name: "Septiembre" },
  { id: 10, name: "Octubre" },
  { id: 11, name: "Noviembre" },
  { id: 12, name: "Diciembre" },
];

const UpdateExpenseModal = ({
  show,
  onHide,
  onSave,
  monthName,
  currentSpent,
  saveLoading,
}) => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    const amountValue = parseFloat(amount);
    if (!amount || isNaN(amountValue) || amountValue <= 0) {
      setError("Ingrese un monto válido mayor a cero");
      return;
    }
    onSave(amountValue);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FiDollarSign className="me-2" />
          Actualizar gasto para {monthName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="expenseAmount">
          <Form.Label>Monto del gasto (L)</Form.Label>
          <Form.Control
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError("");
            }}
          />
          {error && <div className="text-danger mt-2">{error}</div>}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saveLoading}>
          {saveLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Guardando...
            </>
          ) : (
            "Confirmar Gasto"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

function BudgetControl() {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("");
  const onShowSidebar = () => setShow(!show);

  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedMonthlyBudget, setSelectedMonthlyBudget] = useState(null);

  //Store
  const { budgets, loading: loadingBudgets } = useSelector(
    (store) => store.trainingBudgetControl
  );
  const {
    annualBudgets,
    loading: loadingAnnualBudgets,
    annualBudgetsLength,
    saveLoading,
  } = useSelector((store) => store.trainingBudgetControl);
  const userId = useSelector((store) => store.auth.userId);
  const userRoles = useSelector((store) => store.auth.userRoles);

  //Validacion para Responsable de Capacitacion
  let isTrainingManager = false;
  if (userRoles.length > 0 && userRoles.includes("Responsable_Capacitacion")) {
    isTrainingManager = true;
  }

  // Obtiene empresas/zona al montar el componente
  useEffect(() => {
    dispatch(getBudgets());
  }, [dispatch]);

  useEffect(() => {
    if (selectedOrganization) {
      const params = {
        organizacion: selectedOrganization,
        anio: selectedYear,
      };

      if (selectedMonth) {
        params.mes = selectedMonth;
      }

      dispatch(clearAnnualBudgets());
      dispatch(getAnnualBudgets(params));
    } else {
      dispatch(clearAnnualBudgets());
    }
  }, [selectedOrganization, selectedYear, selectedMonth, dispatch]);

  const organizations = budgets.map((budget) => ({
    id: budget.id,
    name: `${budget.organizacion_nombre} / ${budget.empresa_zona_nombre}`,
  }));

  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const years = Array.from(
    { length: Math.max(2, currentYear - startYear + 2) },
    (_, i) => startYear + i
  );

  const processBudgetData = () => {
    if (!annualBudgets || !annualBudgets.presupuesto_anual) {
      return {
        totalAnnualBudget: 0,
        monthlyData: months.map((month) => ({
          month: month.id,
          monthName: month.name,
          assignedBudget: 0,
          spent: 0,
          monthlyBudgetId: null,
        })),
      };
    }

    const monthlyData = months.map((month) => {
      const monthlyBudget = annualBudgets.presupuestos_mensuales?.find(
        (m) => m.mes === month.id
      );

      return {
        month: month.id,
        monthName: month.name,
        assignedBudget: monthlyBudget
          ? parseFloat(monthlyBudget.monto_asignado)
          : 0,
        spent: monthlyBudget ? parseFloat(monthlyBudget.monto_gastado) : 0,
        monthlyBudgetId: monthlyBudget?.id || null,
      };
    });

    return {
      totalAnnualBudget: parseFloat(
        annualBudgets.presupuesto_anual.monto_total
      ),
      monthlyData,
    };
  };

  const budgetData = processBudgetData();

  const filteredData = selectedMonth
    ? budgetData.monthlyData.filter(
        (item) => item.month === parseInt(selectedMonth)
      )
    : budgetData.monthlyData;

  const totalAssigned = filteredData.reduce(
    (sum, month) => sum + month.assignedBudget,
    0
  );
  const totalSpent = filteredData.reduce((sum, month) => sum + month.spent, 0);
  const remainingBudget = totalAssigned - totalSpent;

  const allMonthsHaveBudget =
    annualBudgets?.presupuestos_mensuales?.length === 12;

  const exportData = budgetData.monthlyData.map((month) => ({
    Mes: month.monthName,
    "Presupuesto Asignado (L)": month.assignedBudget,
    "Gastado (L)": month.spent,
    "Diferencia (L)": month.assignedBudget - month.spent,
    "Porcentaje Utilizado":
      month.assignedBudget > 0
        ? `${((month.spent / month.assignedBudget) * 100).toFixed(2)}%`
        : "0%",
  }));

  if (exportData.length > 0) {
    exportData.push({
      Mes: "TOTAL ANUAL",
      "Presupuesto Asignado (L)": budgetData.totalAnnualBudget,
      "Gastado (L)": totalSpent,
      "Diferencia (L)": budgetData.totalAnnualBudget - totalSpent,
      "Porcentaje Utilizado":
        budgetData.totalAnnualBudget > 0
          ? `${((totalSpent / budgetData.totalAnnualBudget) * 100).toFixed(2)}%`
          : "0%",
    });
  }

  const handleAddBudget = () => {
    setShowModal(true);
  };

  const handleEditExpense = (monthData) => {
    setSelectedMonthlyBudget(monthData);
    setShowExpenseModal(true);
  };

  const handleSaveExpense = (amount) => {
    if (selectedMonthlyBudget) {
      const data = {
        monto_gastado: amount,
        usuario_id: userId,
      };
      dispatch(
        updateMonthlyBudgetExpenditure(
          data,
          selectedMonthlyBudget.monthlyBudgetId
        )
      );
    }
  };

  return (
    <>
      <CustomNavbar type={"dashboard"} onShowSidebar={onShowSidebar} />
      <SidebarBoarding show={show} onShowSidebar={onShowSidebar} />
      <div className="custom-wrapper">
        <Container fluid>
          <Row className="my-4">
            <Col>
              <h5 className="text-black-50 m-0 text-uppercase">
                Presupuesto Anual
              </h5>
              <p className="text-black-50 m-0 text-uppercase">
                Control de Presupuesto Anual por Empresas
              </p>
            </Col>
          </Row>

          <Row className="mb-4 justify-content-center">
            <Col md={8} className="d-flex">
              <div className="d-flex flex-wrap gap-3">
                <Form.Group
                  controlId="organizationSelect"
                  className="flex-grow-1"
                  style={{ minWidth: "250px" }}
                >
                  <Form.Label>Empresa / Zona</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedOrganization}
                    onChange={(e) => setSelectedOrganization(e.target.value)}
                    disabled={loadingBudgets}
                  >
                    <option value="">Seleccione empresa/zona</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                <Form.Group
                  controlId="yearSelect"
                  className="flex-grow-1"
                  style={{ minWidth: "120px" }}
                >
                  <Form.Label>Año</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                <Form.Group
                  controlId="monthSelect"
                  className="flex-grow-1"
                  style={{ minWidth: "150px" }}
                >
                  <Form.Label>Mes</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    <option value="">Todos los meses</option>
                    {months.map((month) => (
                      <option key={month.id} value={month.id}>
                        {month.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </div>
            </Col>

            <Col md={4} className="d-flex justify-content-end">
              <div className="d-flex align-items-center">
                <ButtonGroup>
                  {allMonthsHaveBudget && selectedOrganization && (
                    <ExcelFile
                      element={
                        <Button variant="success" className="me-2">
                          <FiDownload className="me-1" /> Exportar
                        </Button>
                      }
                      filename={`Presupuesto_${selectedYear}_${
                        organizations
                          .find((o) => o.id == selectedOrganization)
                          ?.name.replace(/[/\s]+/g, "_") || "Sin_Organizacion"
                      }`}
                    >
                      <ExcelSheet data={exportData} name="Presupuesto">
                        <ExcelColumn label="Mes" value="Mes" />
                        <ExcelColumn
                          label="Presupuesto Asignado (L)"
                          value="Presupuesto Asignado (L)"
                        />
                        <ExcelColumn label="Gastado (L)" value="Gastado (L)" />
                        <ExcelColumn
                          label="Diferencia (L)"
                          value="Diferencia (L)"
                        />
                        <ExcelColumn
                          label="Porcentaje Utilizado"
                          value="Porcentaje Utilizado"
                        />
                      </ExcelSheet>
                    </ExcelFile>
                  )}
                  <Button
                    variant="primary"
                    title="Agregar"
                    onClick={handleAddBudget}
                    disabled={isTrainingManager}
                  >
                    <FiPlus />
                  </Button>
                </ButtonGroup>
              </div>
            </Col>
          </Row>

          {loadingBudgets || loadingAnnualBudgets ? (
            <Row>
              <Col>
                <div className="text-center p-4">
                  <p className="mt-2">
                    Cargando información de presupuestos...
                  </p>
                </div>
              </Col>
            </Row>
          ) : selectedOrganization ? (
            annualBudgetsLength > 0 ? (
              <>
                <Row className="mb-4">
                  <Col md={4}>
                    <Card className="text-center">
                      <Card.Header>
                        {selectedMonth
                          ? "Presupuesto del Mes"
                          : "Presupuesto Total"}
                      </Card.Header>
                      <Card.Body>
                        <Card.Title className="text-success">
                          L
                          {selectedMonth
                            ? filteredData[0]?.assignedBudget.toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              ) || "0.00"
                            : budgetData.totalAnnualBudget.toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                        </Card.Title>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="text-center">
                      <Card.Header>
                        {selectedMonth ? "Gastado en el Mes" : "Total Gastado"}
                      </Card.Header>
                      <Card.Body>
                        <Card.Title className="text-danger">
                          L
                          {totalSpent.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Card.Title>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="text-center">
                      <Card.Header>
                        {selectedMonth
                          ? "Saldo del Mes"
                          : "Presupuesto Restante"}
                      </Card.Header>
                      <Card.Body>
                        <Card.Title
                          className={
                            remainingBudget >= 0
                              ? "text-primary"
                              : "text-danger"
                          }
                        >
                          L
                          {remainingBudget.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Card.Title>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Table striped bordered hover responsive className="mt-3">
                      <thead className="table-dark">
                        <tr>
                          <th>Mes</th>
                          <th>Presupuesto Asignado</th>
                          <th>Gastado</th>
                          <th>Diferencia</th>
                          <th>% Utilizado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((month) => {
                          const difference = month.assignedBudget - month.spent;
                          const percentageUsed =
                            month.assignedBudget > 0
                              ? (month.spent / month.assignedBudget) * 100
                              : 0;

                          return (
                            <tr key={month.month}>
                              <td>{month.monthName}</td>
                              <td>
                                L
                                {month.assignedBudget.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td>
                                L
                                {month.spent.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td
                                className={
                                  difference >= 0
                                    ? "text-success"
                                    : "text-danger"
                                }
                              >
                                L
                                {difference.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="progress flex-grow-1 me-2">
                                    <div
                                      className={`progress-bar ${
                                        percentageUsed > 100
                                          ? "bg-danger"
                                          : "bg-success"
                                      }`}
                                      role="progressbar"
                                      style={{
                                        width: `${Math.min(
                                          percentageUsed,
                                          100
                                        )}%`,
                                      }}
                                      aria-valuenow={percentageUsed}
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                    ></div>
                                  </div>
                                  <span>{percentageUsed.toFixed(1)}%</span>
                                </div>
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditExpense(month)}
                                  disabled={
                                    !month.monthlyBudgetId ||
                                    saveLoading ||
                                    month.spent > 0
                                  }
                                  className={`d-flex justify-content-center align-items-center gap-1 ${
                                    month.spent > 0
                                      ? "text-success border-success"
                                      : ""
                                  }`}
                                >
                                  <FiEdit />
                                  {month.spent > 0
                                    ? " Gasto Registrado"
                                    : "  Añadir Gasto"}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </>
            ) : (
              <Row className="justify-content-center">
                <Col md={8}>
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <FiAlertCircle className="display-4 text-warning mb-3" />
                      <Card.Title className="text-warning">
                        Presupuesto no asignado
                      </Card.Title>
                      <Card.Text>
                        La empresa/zona seleccionada aún no tiene un presupuesto
                        asignado para el año {selectedYear}.
                        <br />
                        Para comenzar, asigne un presupuesto anual.
                      </Card.Text>
                      <Button
                        variant="warning"
                        className="text-white"
                        onClick={handleAddBudget}
                        disabled={isTrainingManager}
                      >
                        <FiPlus className="me-2" />
                        Asignar Presupuesto
                      </Button>
                    </Card.Body>
                    <Card.Footer className="text-muted">
                      Podrá distribuir el presupuesto por meses después de
                      asignar el total anual
                    </Card.Footer>
                  </Card>
                </Col>
              </Row>
            )
          ) : (
            <Row className="justify-content-center">
              <Col md={6}>
                <Card className="text-center border-primary">
                  <Card.Body>
                    <FiInfo className="display-4 text-primary mb-3" />
                    <Card.Title>Seleccione una empresa/zona</Card.Title>
                    <Card.Text>
                      Para visualizar el control de presupuesto, por favor
                      seleccione una empresa/zona y el año correspondiente
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <BudgetModal
            show={showModal}
            onHide={() => setShowModal(false)}
            organizations={organizations}
          />

          {/* Modal para actualizar gasto */}
          <UpdateExpenseModal
            show={showExpenseModal}
            saveLoading={saveLoading}
            onHide={() => setShowExpenseModal(false)}
            onSave={handleSaveExpense}
            monthName={selectedMonthlyBudget?.monthName || ""}
            currentSpent={selectedMonthlyBudget?.spent || 0}
          />
        </Container>
      </div>
    </>
  );
}

export default BudgetControl;
